<template>
    <div>
        <div class="input-container">
            <el-input 
                v-model="url" 
                placeholder="Enter URL to crawl (e.g., https://example.com)" 
                :disabled="showProgress">
            </el-input>
            <el-button 
                type="primary" 
                @click="startCrawl" 
                :disabled="!url || showProgress">
                Start Crawl
            </el-button>
        </div>

        <div v-if="showProgress" class="progress-container" :class="{ 'crawl-active': !chainDone, 'crawl-complete': chainDone }">
            <div class="progress-grid">
                <div v-for="(step, id) in progressSteps" :key="id" class="progress-card">
                    <div class="card-header">{{ step.name }}</div>
                    <div class="card-body">
                        <el-progress 
                            :percentage="step.percentage" 
                            :stroke-width="8"
                            :show-text="false">
                        </el-progress>
                        <div class="progress-text">
                            <span v-if="step.current_count_total > 0">
                                ({{ step.current_count_done | commas }}/{{ step.current_count_total | commas }})
                            </span>
                            <span v-else-if="step.percentage === 0">
                                (not yet started)
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            <div class="status-container">
                <span class="status-text">{{ statusText }}</span>
                <el-button v-if="showProgress && !chainDone" type="danger" size="small" @click="cancelCrawl">Cancel</el-button>
            </div>

            <div v-if="crawl_step_id && screenshots.length > 0" class="screenshots-container">
                <div class="screenshots-grid">
                    <div v-for="n in 3" :key="n" class="screenshot-slot">
                        <img v-if="screenshots[n-1]" 
                             :src="screenshots[n-1].url" 
                             :alt="screenshots[n-1].title"
                             class="screenshot-image">
                        <div v-else class="screenshot-placeholder">
                            Screenshot {{ n }}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    let done_sound;
    let warning_sound;
    let error_sound;
    
    export default {
        filters: {
            commas: function(value) {
                if (value) {
                    return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                } else {
                    return value
                }
            }
        },
        data() {
            return {
                url: '',
                progressSteps: {},
                showProgress: false,
                statusText: '',
                job_chain_id: null,
                chainDone: false,
                extent_id: null,
                
                // Crawl definition related properties
                crawl_definitions: null,
                global_definition: null,
                extent_definition: null,
                last_definition: null,
                best_default_definition_type: null,
                best_default_definition: null,
                
                // For tracking the selected crawl definition
                selected_definition_type: null,
                selected_definition: null,
                crawl_definition_id: null,
                committed_definition: {
                    definition: {
                        id: null
                    }
                },
                
                // Screenshot related
                crawl_step_id: null,
                screenshots: [],
                screenshot_version: 1,
                last_screenshot_timestamp: null
            }
        },
        methods: {
            getDefinitions() {
                if (this.extent_id) {
                    axios.get('/extents/get_all_crawl_definitions/' + this.extent_id)
                        .then(response => {
                          console.log(response)
                            this.crawl_definitions = response.data;
                            this.crawl_definitions.forEach((definition) => {
                                if (definition.type.label === 'global default') {
                                    this.global_definition = definition;
                                } else if (definition.type.label === 'job specific') {
                                    this.last_definition = definition;
                                } else if (definition.type.label === 'extent default') {
                                    this.extent_definition = definition;
                                }
                            });
                            
                            if (this.extent_definition) {
                                this.best_default_definition = this.extent_definition;
                                this.best_default_definition_type = 'extent default';
                            } else {
                                this.best_default_definition = this.global_definition;
                                this.best_default_definition_type = 'global default';
                            }

                            this.crawl_definition_id = this.best_default_definition.id
                            
                        })
                        .catch(error => {
                            console.error('Could not load crawl definitions:', error);
                            this.statusText = "Error loading crawl definitions";
                        });
                }
            },
            
            getBasics() {
                this.statusText = "Retrieving site information...";
                
                return axios.get('/easy/basics', {
                    params: { url: this.url }
                }).then(response => {
                    console.log('getBasics response:', response.data);
                    console.log('getBasics response.data.site_name:', response.data.site_name);
                    return response.data.site_name;
                }).catch(error => {
                    console.error('Error getting site basics:', error);
                    return this.url; // Fallback to using the URL itself
                });
            },
            
            startCrawl(override = false) {
                if (!this.url) {
                    this.statusText = "Please enter a URL";
                    return;
                }
                
                this.statusText = "Starting crawl...";
                
                // First get the basics about the site
                this.getBasics().then(siteName => {
                  console.log('in startCrawl, siteName:', siteName);
                    // Create a new extent using the site name
                    axios.post('/extents', {
                        site_name: siteName,
                        parent_id: null,
                        url: this.url,
                    }).then(extentResponse => {
                        this.extent_id = extentResponse.data.extent_id;
                        // Now get the default definitions for this extent
                        this.getDefinitions();
                        this.initiatePreflightCheck()
                    }).catch(error => {
                        console.error('Error creating extent:', error);
                        this.statusText = "Error creating extent";
                    });
                });
            },

            initiatePreflightCheck(override = false) {
                // Preflight check
                let options = {
                    extent_id: this.extent_id,
                    data_source_id: 1, // Using the inventory captain crawler ID
                    crawl_definition_id: this.crawl_definition_id,
                    type: "crawl",
                    override: override,
                    url: this.url
                }
                console.log(options)

                axios.post('/api/jobChains/preflight', options).then(response => {
                    let preflight_response = response.data;
                    
                    if (override || (preflight_response.status === 'ready')) {
                        this.initiateCrawl(options);
                    } else if (preflight_response.status === 'warning') {
                        // Show warning and allow override
                        this.statusText = "Warning: " + preflight_response.errors.map(e => e.text).join(', ');
                        // Add override button functionality here if needed
                    } else {
                        // Show error
                        this.statusText = "Error: " + preflight_response.errors.map(e => e.text).join(', ');
                    }
                }).catch(error => {
                    console.error('Error during preflight check:', error);
                    this.statusText = "Error during preflight check";
                });
            },

            initiateCrawl(options) {
                axios.post('/api/jobChains', options).then(response => {
                    this.job_chain_id = response.data.job_chain_id;
                    this.showProgress = true;
                    this.statusText = "Initializing crawl...";
                    
                    // Get job chain info to set up progress bars
                    axios.get('/api/jobChains/' + this.job_chain_id)
                        .then(response => {
                            response.data.job_chain_steps.forEach((step) => {
                                let tempStep = {
                                    name: step.job_chain_type_step.display_name,
                                    percentage: 0,
                                    current_count_done: 0,
                                    current_count_total: 0,
                                    highest_time_received: 0,
                                    elapsed: ''
                                }
                                this.$set(this.progressSteps, step.id, tempStep);
                                
                                // Identify the crawl step for screenshots
                                if (tempStep.name === "Crawl") {
                                    this.crawl_step_id = step.id;
                                }
                            });
                            
                            // Load initial screenshots if we have a crawl step
                            if (this.crawl_step_id) {
                                this.loadScreenshots();
                            }
                            
                            this.listenForUpdates();
                            // Trigger job chain events
                            axios.post('/api/jobChains/trigger/' + this.job_chain_id)
                                .then(() => {
                                    this.statusText = "Crawl started";
                                })
                                .catch(error => {
                                    console.error('Error triggering job chain:', error);
                                    this.statusText = "Error starting crawl";
                                });
                        })
                        .catch(error => {
                            console.error('Error getting job chain info:', error);
                            this.statusText = "Error initializing crawl";
                        });
                }).catch(error => {
                    console.error('Error starting crawl:', error);
                    this.statusText = "Error starting crawl";
                });
            },

            listenForUpdates() {
                Echo.channel('job_chain_status.' + this.job_chain_id)
                    .listen('JobProgressUpdate', (event) => {
                        this.handleProgressUpdate(event);
                    });
            },

            handleProgressUpdate(event) {
                // Update progress bar for the specific step
                if (this.progressSteps[event.job_step_id]) {
                    if (event.time > this.progressSteps[event.job_step_id].highest_time_received) {
                        this.progressSteps[event.job_step_id].highest_time_received = event.time;
                        this.progressSteps[event.job_step_id].percentage = event.percentage;
                        this.progressSteps[event.job_step_id].current_count_done = event.current_count_done;
                        this.progressSteps[event.job_step_id].current_count_total = event.current_count_total;
                        this.progressSteps[event.job_step_id].elapsed = event.elapsed;
                    }
                }

                // Handle chain status
                if (event.chain_status === "canceled") {
                    this.chime('warning');
                    this.chainDone = true;
                    this.statusText = "Crawl canceled";
                } else if (event.chain_status === "done") {
                    this.chime('done');
                    this.chainDone = true;
                    this.statusText = "Crawl completed successfully";
                } else if (event.chain_status === "met_limit") {
                    this.chime('warning');
                    this.chainDone = true;
                    this.statusText = "Crawl reached limit";
                } else if (event.chain_status === "failed") {
                    this.chime('error');
                    this.chainDone = true;
                    this.statusText = "Crawl failed";
                    
                    // Get more error details if available
                    axios.get("/progress/status/" + event.job_step_id)
                        .then(response => {
                            this.statusText = "Failed: " + response.data.error_message;
                        })
                        .catch(() => {
                            this.statusText = "Failed with unknown error";
                        });
                } else if (!this.chainDone && event.current_count_done > 0) {
                    this.statusText = "Crawling in progress...";
                }
                
                // Update screenshots periodically during crawl
                this.updateScreenshots();
            },

            loadScreenshots() {
                if (!this.crawl_step_id) return;
                
                axios.get('/api/jobChains/screenshots/' + this.job_chain_id + '?v=' + this.screenshot_version)
                    .then(response => {
                        // Keep only the latest 3 screenshots
                        this.screenshots = response.data.slice(-3);
                    })
                    .catch(error => {
                        console.error('Error loading screenshots:', error);
                    });
            },

            updateScreenshots() {
                if (!this.crawl_step_id) return;
                
                let current_timestamp = new Date();
                
                // Update screenshots every 15 seconds during active crawl
                if (!this.last_screenshot_timestamp || 
                    (current_timestamp.getTime() - this.last_screenshot_timestamp.getTime()) > 15 * 1000) {
                    
                    this.last_screenshot_timestamp = current_timestamp;
                    this.screenshot_version++;
                    this.loadScreenshots();
                }
            },

            cancelCrawl() {
                if (this.job_chain_id) {
                    axios.post('/api/jobChains/cancel/' + this.job_chain_id)
                        .then(() => {
                            this.statusText = "Cancellation requested";
                        })
                        .catch(error => {
                            console.error('Error canceling crawl:', error);
                            this.statusText = "Error canceling crawl";
                        });
                }
            },

            chime(event) {
                if (event === 'done') {
                    done_sound.play();
                } else if (event === 'warning') {
                    warning_sound.play();
                } else if (event === 'error') {
                    error_sound.play();
                }
            }
        },

        mounted() {
            // Initialize sound objects
            done_sound = new window.Howl({
                src: ['/audio/music_flute_welcome.wav']
            });
            warning_sound = new window.Howl({
                src: ['/audio/music_piano_warning.wav']
            });
            error_sound = new window.Howl({
                src: ['/audio/music_timpani_error_05.wav']
            });
        }
    }
</script>

<style scoped>
.input-container {
    display: flex;
    margin-bottom: 20px;
}

.input-container .el-input {
    flex-grow: 1;
    margin-right: 10px;
}

.progress-container {
    margin-top: 15px;
    padding: 20px;
    border-radius: 8px;
    transition: background 0.5s ease;
}

.progress-container.crawl-active {
    background: linear-gradient(-45deg, #e3f2fd, #f3e5f5, #e8f5e8, #fff3e0);
    background-size: 400% 400%;
    animation: gradientShift 4s ease-in-out infinite;
}

.progress-container.crawl-complete {
    background: #f5f5f5;
}

@keyframes gradientShift {
    0% {
        background-position: 0% 50%;
    }
    50% {
        background-position: 100% 50%;
    }
    100% {
        background-position: 0% 50%;
    }
}

.progress-grid {
    display: flex;
    gap: 10px;
    margin-bottom: 15px;
}

.progress-card {
    flex: 1;
    border: 1px solid #ebeef5;
    border-radius: 4px;
    padding: 10px;
    background: #fafafa;
    min-height: 60px;
}

.card-header {
    font-weight: 600;
    font-size: 0.9em;
    margin-bottom: 8px;
    color: #303133;
}

.card-body {
    display: flex;
    flex-direction: column;
    gap: 5px;
}

.progress-text {
    font-size: 0.8em;
    color: #606266;
    text-align: center;
}

.status-container {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px 0;
    border-top: 1px solid #ebeef5;
}

.status-text {
    font-size: 0.9em;
    color: #606266;
}

.screenshots-container {
    margin: 15px 0;
}

.screenshots-grid {
    display: flex;
    gap: 10px;
}

.screenshot-slot {
    flex: 1;
    height: 120px;
    border: 1px solid #ebeef5;
    border-radius: 4px;
    overflow: hidden;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #fafafa;
}

.screenshot-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.screenshot-placeholder {
    color: #909399;
    font-size: 0.8em;
    text-align: center;
}
</style>

